/* JEANNETTE: one colour per set and a size guide available at the size selector. */
(() => {
  'use strict';
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\b(zebre|zebra)\b/g, 'zebra').replace(/\s*&\s*/g, ' et ').replace(/\s+/g, ' ').trim();
  const isSize = name => /taille|size/i.test(name || '');
  const isColour = name => /couleur|colou?r|motif|pattern|animal|signe/i.test(name || '');
  const json = (element, fallback) => { try { return JSON.parse(element.textContent); } catch { return fallback; } };
  const emitChange = element => element.dispatchEvent(new Event('change', { bubbles: true }));

  class CoordinatedEnsemble extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      if (!this.querySelector('[data-jn-messages]')) {
        this.frame = requestAnimationFrame(() => { if (this.isConnected) this.connectedCallback(); });
        return;
      }
      this.controller = new AbortController();
      const on = (target, name, callback) => target.addEventListener(name, callback, { signal: this.controller.signal });
      this.picker = this.querySelector('[data-jn-collection]');
      this.colourPicker = this.querySelector('[data-set-colour]');
      this.addButton = this.querySelector('[data-jn-add]');
      this.status = this.querySelector('[data-jn-status]');
      this.words = json(this.querySelector('[data-jn-messages]'), {});
      this.format = new Intl.NumberFormat(this.dataset.locale || 'fr', { style: 'currency', currency: this.dataset.currency || 'EUR' });
      this.busy = false; this.coordinating = false; this.uncertain = false;
      this.state = { items: [], colours: {} };
      try {
        const saved = JSON.parse(sessionStorage.getItem('jeannette-set-v5') || sessionStorage.getItem('jeannette-set-v4') || 'null');
        if (saved && Array.isArray(saved.items)) this.state = { ...saved, colours: saved.colours || {} };
      } catch {}
      const params = new URLSearchParams(location.search);
      let requested = params.get('collection');
      if (requested && ![...this.picker.options].some(option => option.value === requested) && !requested.startsWith('collection-')) requested = 'collection-' + requested;
      const collection = requested || this.state.collection;
      if ([...this.picker.options].some(option => option.value === collection)) this.picker.value = collection;
      this.carriedColour = params.get('color');
      on(this, 'change', event => {
        if (this.coordinating || this.busy) return;
        if (event.target === this.picker) { this.carriedColour = null; this.mount(); return; }
        if (event.target === this.colourPicker) { this.setColour(this.colourPicker.value); return; }
        const card = event.target.closest('[data-item]');
        if (!card || !card.model || card.closest('[data-jn-panel]') !== this.panel) return;
        if (event.target.matches('[data-choice]')) {
          if (Number(event.target.dataset.choice) === card.model.colourPosition) {
            this.setColour(normalize(event.target.value));
          } else {
            this.resolve(card);
            if (card.model.resolved?.available) card.querySelector('[data-include]').checked = true;
            this.totals();
          }
        } else if (event.target.matches('[data-include]')) this.totals();
      });
      on(this, 'click', event => {const button=event.target.closest('[data-jn-family]');if(!button||this.busy)return;this.picker.value=button.dataset.jnFamily;this.carriedColour=null;this.mount();});
      on(this.addButton, 'click', () => this.purchase());
      on(window, 'pageshow', () => { if (!this.busy) this.totals(); });
      this.mount();
      customElements.whenDefined('jn-size-assistant').then(() => requestAnimationFrame(() => { if (this.isConnected && !this.busy) this.setColour(this.colour || ''); }));
    }
    disconnectedCallback() { this.controller?.abort(); this.controller = null; cancelAnimationFrame(this.frame); }
    payable(cents) {const offer=this.querySelector('[data-offer-total]');return Math.round(Number(cents||0)*(100-Number(offer?.dataset.percent||0))/100);}
    money(cents) { return this.format.format(Number(cents || 0) / 100); }
    cards() { return [...this.panel.querySelectorAll('[data-item]')]; }
    choices(card) { return [...card.querySelectorAll('[data-choice]')]; }
    initCard(card) {
      if (card.model) return;
      const options = json(card.querySelector('[data-set-options-json]'), []);
      const variants = [...card.querySelector('[data-variant]').options].filter(option => option.value).map(option => ({
        id: option.value, values: json({ textContent: option.dataset.options }, []), available: !option.disabled,
        price: Number(option.dataset.price), image: option.dataset.image || '', title: option.dataset.displayVariant || '', note: option.dataset.note || ''
      }));
      card.model = { options, variants, colourPosition: options.findIndex(isColour), personal: card.dataset.personalizable === 'true', resolved: null };
      const previous = this.state.items.find(item => item.id === card.dataset.item);
      if (previous) {
        const variant = variants.find(v => v.id === previous.variant);
        const values = variant?.values || previous.choices;
        if (Array.isArray(values)) this.choices(card).forEach(select => { const value = values[Number(select.dataset.choice)]; if ([...select.options].some(o => o.value === value)) select.value = value; });
        const include = card.querySelector('[data-include]');
        if (include) include.checked = !!previous.selected;
      }
      this.choices(card).forEach(select => {
        const position = Number(select.dataset.choice);
        if (!select.value && select.options.length === 2 && !isSize(options[position])) select.value = select.options[1].value;
        if (position === card.model.colourPosition) {
          select.hidden = true;
          const label = card.querySelector('label[for="' + select.id + '"]');
          if (label) label.hidden = true;
        }
      });
    }
    mount() {
      let panel = [...this.querySelectorAll('[data-jn-panel]')].find(p => p.dataset.jnPanel === this.picker.value);
      if (!panel) {
        const template = [...this.querySelectorAll('[data-jn-template]')].find(t => t.dataset.jnTemplate === this.picker.value);
        if (!template) return;
        const fragment = template.content.cloneNode(true);
        panel = fragment.querySelector('[data-jn-panel]');
        this.querySelector('[data-jn-panels]').append(fragment);
      }
      this.panel = panel;
      this.querySelectorAll('[data-jn-family]').forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.jnFamily===this.picker.value)));
      this.querySelectorAll('[data-jn-panel]').forEach(p => { p.hidden = p !== panel; });
      this.cards().forEach(card => this.initCard(card));
      const coloured = this.cards().filter(card => card.model.colourPosition >= 0);
      const anchor = coloured.find(card => /harnais/.test(card.dataset.productHandle)) || coloured[0];
      this.colourPicker.replaceChildren();
      this.querySelector('[data-set-colour-wrap]').hidden = !anchor;
      if (!anchor) { this.setColour(''); return; }
      const seen = new Set();
      for (const variant of anchor.model.variants) {
        const label = variant.values[anchor.model.colourPosition], key = normalize(label);
        if (!key || seen.has(key)) continue;
        seen.add(key); this.colourPicker.add(new Option(label, key));
      }
      const supported = [...this.colourPicker.options].map(option => ({
        option,
        score: coloured.reduce((sum, card) => { const matches = card.model.variants.filter(v => v.available && normalize(v.values[card.model.colourPosition]) === option.value); return sum + (matches.length ? 100 : 0) + Number(matches.some(v => v.image)); }, 0)
      }));
      const prior = normalize(this.carriedColour || this.state.colours[panel.dataset.jnPanel] || '');
      const savedAnchor = this.choices(anchor).find(s => Number(s.dataset.choice) === anchor.model.colourPosition)?.value;
      const preferred = supported.find(entry => entry.option.value === prior) ||
        (!prior && savedAnchor ? supported.find(entry => entry.option.value === normalize(savedAnchor)) : null) ||
        supported.reduce((best, entry) => !best || entry.score > best.score ? entry : best, null);
      this.setColour(preferred?.option.value || '');
    }
    setColour(key) {
      this.coordinating = true;
      this.colourPicker.value = key;
      this.colour = key;
      this.state.colours[this.panel.dataset.jnPanel] = key;
      for (const card of this.cards()) {
        const m = card.model;
        this.choices(card).forEach(select => {
          if (Number(select.dataset.choice) === m.colourPosition) {
            const option = [...select.options].find(o => o.value && normalize(o.value) === key);
            select.value = option?.value || '';
            emitChange(select);
          }
        });
        this.resolve(card);
        const assistant = card.querySelector('jn-size-assistant');
        if (assistant?.ready) {
          const size = this.choices(card).find(s => isSize(m.options[Number(s.dataset.choice)]));
          if (!assistant.requested && size?.value) { assistant.source = 'manual'; assistant.apply(size.value); }
          else assistant.syncPurchaseState();
        }
        this.resolve(card);
      }
      this.coordinating = false;
      this.totals();
    }
    resolve(card) {
      const m = card.model, selects = this.choices(card);
      const byColour = m.variants.filter(v => m.colourPosition < 0 ? !this.colour : normalize(v.values[m.colourPosition]) === this.colour);
      const exact = !m.personal && selects.every(select => !!select.value) ? byColour.find(v => selects.every(select => v.values[Number(select.dataset.choice)] === select.value)) : null;
      m.resolved = exact || null;
      card.querySelector('[data-variant]').value = exact?.id || '';
      const imageCandidates = byColour.filter(v => selects.filter(s => Number(s.dataset.choice) !== m.colourPosition && !isSize(m.options[Number(s.dataset.choice)])).every(s => !s.value || v.values[Number(s.dataset.choice)] === s.value));
      const visual = exact?.image ? exact : imageCandidates.find(v => v.available && v.image) || imageCandidates.find(v => v.image);
      const link = card.querySelector('.jn-set-image'), img = link.querySelector('img'), missing = link.querySelector('[data-image-missing]');
      if (visual?.image) { img.removeAttribute('srcset'); img.src = visual.image; img.alt = card.querySelector('h2').textContent.trim() + ' — ' + visual.title; }
      else { img.removeAttribute('src'); img.removeAttribute('srcset'); }
      img.hidden = !visual?.image;
      missing.hidden = !!visual?.image;
      card.dataset.styleReady = 'true';
      const reference = exact || visual || byColour[0];
      const url = new URL(card.dataset.productUrl, location.origin);
      if (reference) url.searchParams.set('variant', reference.id);
      card.querySelectorAll('[data-set-product-link]').forEach(a => { a.href = url.href; });
      const sameColourInStock = byColour.some(v => v.available);
      const include = card.querySelector('[data-include]');
      if (include) { include.disabled = !sameColourInStock || this.busy; if (!sameColourInStock) include.checked = false; }
      const personal = card.querySelector('[data-set-personalize]');
      if (personal) { personal.hidden = !byColour.length; }
      const colourText = card.querySelector('[data-set-colour-text]');
      colourText.textContent = this.colourPicker.selectedOptions[0]?.textContent || '';
      colourText.hidden = !colourText.textContent;
      const note = card.querySelector('[data-set-variant-note]');
      note.textContent = !byColour.length ? this.words.noColour : !sameColourInStock ? this.words.colourSoldOut : exact && !exact.available ? this.words.sizeSoldOut : exact?.note || '';
      note.hidden = !note.textContent;
      const priceRows = byColour.filter(v => v.available);
      card.querySelector('.jn-set-price').textContent = exact ? this.money(this.payable(exact.price)) : priceRows.length ? this.words.from + ' ' + this.money(this.payable(Math.min(...priceRows.map(v => v.price)))) : '';
      selects.forEach(select => {
        const index = Number(select.dataset.choice);
        if (index === m.colourPosition) return;
        [...select.options].filter(o => o.value).forEach(option => {
          option.disabled = !byColour.some(v => v.available && v.values[index] === option.value && selects.filter(s => Number(s.dataset.choice) !== index && !isSize(m.options[Number(s.dataset.choice)])).every(s => !s.value || v.values[Number(s.dataset.choice)] === s.value));
        });
      });
    }
    selected() { return this.cards().filter(card => !card.model.personal && card.querySelector('[data-include]')?.checked); }
    persist() {
      const all = new Map(this.state.items.map(item => [item.id, item]));
      this.querySelectorAll('[data-item]').forEach(card => {
        if (!card.model || card.model.personal) return;
        all.set(card.dataset.item, { id: card.dataset.item, selected: !!card.querySelector('[data-include]').checked, variant: card.model.resolved?.id || '', choices: this.choices(card).map(s => s.value) });
      });
      this.state.items = [...all.values()]; this.state.collection = this.picker.value;
      try { sessionStorage.setItem('jeannette-set-v5', JSON.stringify(this.state)); } catch {}
    }
    totals() {
      if (!this.panel) return;
      const selected = this.selected();
      const valid = selected.length > 0 && selected.every(card => card.model.resolved?.available);
      const total = selected.reduce((sum, card) => sum + Number(card.model.resolved?.available ? card.model.resolved.price : 0), 0);
      const list = this.querySelector('[data-recap]'); list.replaceChildren();
      this.querySelector('[data-recap-wrap]').hidden = !selected.length;
      for (const card of selected) {
        const v = card.model.resolved; if (!v?.available) continue;
        const li = document.createElement('li'), copy = document.createElement('div'), name = document.createElement('strong'), detail = document.createElement('span'), remove = document.createElement('button');
        if (v.image) { const image = new Image(64, 64); image.src = v.image; image.alt = ''; li.append(image); }
        name.textContent = card.querySelector('h2').textContent.trim(); detail.textContent = v.title + ' — ' + this.money(this.payable(v.price));
        remove.type = 'button'; remove.textContent = this.words.remove; remove.setAttribute('aria-label', this.words.remove + ' ' + name.textContent);
        remove.addEventListener('click', () => { if (this.busy) return; card.querySelector('[data-include]').checked = false; this.totals(); });
        copy.append(name, detail); li.append(copy, remove); list.append(li);
      }
      this.querySelector('[data-jn-total]').textContent = this.money(total);
      const offer = this.querySelector('[data-offer-total]');
      const payable = offer ? selected.reduce((sum, card) => sum + Math.round(Number(card.model.resolved?.available ? card.model.resolved.price : 0) * (100 - Number(offer.dataset.percent)) / 100), 0) : total;
      if (offer) { offer.hidden = !valid; offer.textContent = offer.dataset.label.replace('__AMOUNT__', this.money(payable)); }
      const shipping = this.querySelector('[data-set-shipping]'); shipping.hidden = !valid || this.dataset.currency !== 'EUR';
      shipping.textContent = payable >= 6000 ? this.words.reached : this.words.remaining.replace('__AMOUNT__', this.money(6000 - payable));
      this.addButton.disabled = this.busy || this.uncertain || !!window.JeannetteCartUncertain || !valid;
      if (!this.busy) { this.addButton.textContent = this.words.add + ' — ' + this.money(payable); if (!this.uncertain) this.status.textContent = valid ? this.words.ready : this.words.empty; }
      this.persist();
    }
    async purchase() {
      if (this.addButton.disabled || this.busy || window.JeannetteCartBusy) return;
      this.cards().forEach(card => this.resolve(card)); this.totals(); if (this.addButton.disabled) return;
      const service = window.JeannettePurchase;
      if (!service) { this.status.textContent = this.words.error; return; }
      const lines = this.selected().map(card => ({ id: Number(card.model.resolved.id), quantity: 1 }));
      this.busy = true; this.setAttribute('aria-busy', 'true'); this.addButton.disabled = true;
      const controls = [...this.querySelectorAll('select:not([hidden]),input,button')].map(el => [el, el.disabled]);
      controls.forEach(([el]) => { el.disabled = true; });
      service.feedback(this.status, { kind: 'pending' });
      let result;
      try { result = await service.addLines(lines, { source: 'jeannette-ensemble', dedupe: false }); this.uncertain = !!result.uncertain; }
      catch { result = { kind: 'unconfirmed', uncertain: true }; this.uncertain = true; }
      finally {
        this.busy = false; this.removeAttribute('aria-busy'); controls.forEach(([el, disabled]) => { if (el.isConnected) el.disabled = disabled; });
        this.cards().forEach(card => this.resolve(card)); this.totals(); service.feedback(this.status, result);
      }
    }
  }
  if (!customElements.get('jn-coordinated-ensemble')) customElements.define('jn-coordinated-ensemble', CoordinatedEnsemble);

  /* Preserve the existing sizing engine; only move its existing controls into a dialog. */
  const guides = new WeakMap();
  const openGuides = new Set();
  let previousOverflow = '';
  const lockScroll = dialog => {
    if (!openGuides.size) { previousOverflow = document.documentElement.style.overflow; document.documentElement.style.overflow = 'hidden'; }
    openGuides.add(dialog);
  };
  const unlockScroll = dialog => {
    openGuides.delete(dialog);
    if (!openGuides.size) document.documentElement.style.overflow = previousOverflow;
  };
  function prepareGuide(assistant) {
    if (guides.has(assistant)) return guides.get(assistant);
    if (!assistant.ready || typeof assistant.renderGuide !== 'function') return null;
    const trigger = assistant.querySelector('[data-size-help-toggle]'), panel = assistant.querySelector('[data-size-help-panel]');
    if (!trigger || !panel) return null;
    const fr = (assistant.dataset.language || 'fr').startsWith('fr');
    const dialog = document.createElement('dialog'); dialog.className = 'jn-direct-size-guide';
    dialog.id = panel.id + '-dialog'; dialog.setAttribute('aria-label', assistant.words.guide);
    const header = document.createElement('header'), heading = document.createElement('h2'), close = document.createElement('button');
    heading.textContent = assistant.words.guide; close.type = 'button'; close.textContent = '×'; close.className = 'jn-direct-size-close';
    close.setAttribute('aria-label', fr ? 'Fermer le guide des tailles' : 'Close size guide'); close.autofocus = true;
    header.append(heading, close);
    const footer = document.createElement('footer'), back = document.createElement('button');
    back.type = 'button'; back.className = 'jn-direct-size-return'; back.textContent = fr ? 'Revenir au choix de taille' : 'Back to size selection'; footer.append(back);
    dialog.append(header); assistant.append(dialog); dialog.append(panel, footer);
    panel.hidden = false;
    const chart = panel.querySelector('[data-inline-chart]'); chart.open = true; chart.querySelector('summary').hidden = true;
    const main = panel.querySelector('.jn-size-main');
    const instructions = main.querySelector('[data-jn-measuring-instructions]');
    const help = document.createElement('section'); help.className = 'jn-direct-measure-help';
    const summary = document.createElement('h3'); summary.textContent = assistant.dataset.enterMeasurements; help.append(summary);
    [...main.children].filter(el => el !== chart && el !== instructions).forEach(el => help.append(el));
    main.append(help, chart); if (instructions) main.prepend(instructions);
    help.hidden = !assistant.grid;
    trigger.textContent = assistant.dataset.findSize;
    trigger.setAttribute('aria-haspopup', 'dialog'); trigger.setAttribute('aria-controls', dialog.id);
    let pageX = 0, pageY = 0;
    const repaint = () => {
      for (const row of chart.querySelectorAll('tbody tr')) {
        const size = row.cells[0]?.textContent.trim();
        const original = [...assistant.querySelectorAll('[data-manual-size]')].find(b => b.textContent.trim() === size);
        if (!original) continue;
        let button = row.querySelector('[data-guide-choose]');
        if (!button) {
          button = document.createElement('button'); button.type = 'button'; button.dataset.guideChoose = original.dataset.manualSize; button.textContent = size;
          row.cells[0].replaceChildren(button);
          button.addEventListener('click', () => { if (button.disabled) return; assistant.source = 'manual'; assistant.apply(button.dataset.guideChoose); dialog.close(); });
        }
        button.disabled = original.disabled; button.setAttribute('aria-pressed', original.getAttribute('aria-pressed') || 'false');
        row.toggleAttribute('data-size-match', original.dataset.measureMatch === 'true');
      }
      const result = assistant.querySelector('[data-size-result]');
      if (dialog.open && result?.textContent.includes('Choisissez entre ces tailles ci-dessus')) result.textContent = result.textContent.replace('Choisissez entre ces tailles ci-dessus', 'Choisissez dans le tableau');
      if (dialog.open && result?.textContent.includes('Choose between these sizes above')) result.textContent = result.textContent.replace('Choose between these sizes above', 'Choose in the chart');
      back.textContent = assistant.chosen ? (fr ? 'Garder la taille ' : 'Keep size ') + assistant.label(assistant.requested) : fr ? 'Revenir au choix de taille' : 'Back to size selection';
    };
    const open = () => {
      if (dialog.open) return;
      pageX = window.scrollX; pageY = window.scrollY;
      assistant.renderGuide(); chart.open = true; chart.querySelector('summary').hidden = true;
      if (!assistant.grid) {
        const contact = assistant.querySelector('[data-size-contact]'); if (contact) { contact.hidden = false; main.append(contact); }
      }
      repaint(); trigger.setAttribute('aria-expanded', 'true'); lockScroll(dialog);
      dialog.showModal(); close.focus({ preventScroll: true }); panel.scrollTop = 0;
    };
    trigger.addEventListener('click', event => { event.preventDefault(); event.stopImmediatePropagation(); open(); }, true);
    close.addEventListener('click', () => dialog.close()); back.addEventListener('click', () => dialog.close());
    dialog.addEventListener('click', event => {
      if (event.target !== dialog) return;
      const r = dialog.getBoundingClientRect();
      if (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) dialog.close();
    });
    dialog.addEventListener('close', () => {
      unlockScroll(dialog); trigger.setAttribute('aria-expanded', 'false');
      if (assistant.isConnected) { trigger.focus({ preventScroll: true }); window.scrollTo({ left: pageX, top: pageY, behavior: 'instant' }); }
    });
    const observer = new MutationObserver(() => { if (dialog.open) repaint(); });
    observer.observe(assistant.querySelector('[data-manual-sizes]'), { subtree: true, attributes: true, attributeFilter: ['aria-pressed', 'data-measure-match', 'disabled'] });
    observer.observe(assistant.querySelector('[data-size-result]'), { childList: true, subtree: true, characterData: true });
    observer.observe(chart.querySelector('[data-inline-chart-content]'), { childList: true, subtree: true });
    const state = { open, dialog, observer }; guides.set(assistant, state); return state;
  }
  document.addEventListener('click', event => {
    const trigger = event.target instanceof Element && event.target.closest('[data-size-help-toggle]');
    const assistant = trigger && trigger.closest('jn-size-assistant');
    if (!assistant) return;
    const state = prepareGuide(assistant);
    if (state) { event.preventDefault(); event.stopImmediatePropagation(); state.open(); }
  }, true);
  const scan = container => container.querySelectorAll('jn-size-assistant').forEach(assistant => prepareGuide(assistant));
  customElements.whenDefined('jn-size-assistant').then(() => { scan(document); });
  document.addEventListener('DOMContentLoaded', () => scan(document));
  document.addEventListener('shopify:section:load', event => scan(event.target));
  const observer = new MutationObserver(records => {
    for (const record of records) {
      for (const node of record.addedNodes) if (node instanceof Element) {
        const assistant = node.closest('jn-size-assistant');
        if (assistant) prepareGuide(assistant);
        else scan(node);
      }
      for (const node of record.removedNodes) if (node instanceof Element) {
        const removed = node.matches('jn-size-assistant') ? [node] : [...node.querySelectorAll('jn-size-assistant')];
        removed.forEach(assistant => { if (!assistant.isConnected) { const state = guides.get(assistant); state?.observer.disconnect(); if (state?.dialog.open) unlockScroll(state.dialog); } });
      }
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
})();

